import React from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Label, Input, FormGroup, Modal, ModalBody, ModalFooter} from 'reactstrap';
import {find, some, remove, join} from 'lodash';
import {
  SHORT_DATE_FORMAT, IsCurrentUser, validationCreator, URL_NewClientLocation,
  URL_Locations, URL_Clients, BASE_URL
} from '../../constants';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import moment from 'moment';
import {apiRequest, apiPost, apiPut} from "../../utils/request";
import TextBoxControl from '../Common/TextBoxControl';
import {toastr} from 'react-redux-toastr';
import { Link } from 'react-router-dom';

const ValidationRules = {
  locationClientId: {
    validator: (val, state) => {
      let exp = /^[c|C|l|L]\d+$/;
      return !exp.test(val) ? validationCreator(false, 'Please enter a valid Client or Location Id') : validationCreator();
    }
  },
};

class UserAuthenticationRequestsControl extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      authorisationRequest: {
        locationClientId: '',
      },
      validationMessages: {},
      user: null,
      isModalOpen: false,
      isAuthRequestModalOpen: false,
      isRevokeModalOpen: false,
      isLinkClientLocationModalOpen: false,
    };

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleSubmitAuthorisationRequest = this.handleSubmitAuthorisationRequest.bind(this);
    this.loadUser = this.loadUser.bind(this);
    this.actionRequest = this.actionRequest.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
    this.toggleRevokeModal = this.toggleRevokeModal.bind(this);
    this.revokeRequest = this.revokeRequest.bind(this);
  }

  handleFieldChange(e) {
    let authorisationRequest = {...this.state.authorisationRequest};
    authorisationRequest[`${e.target.name}`] = e.target.value;
    this.setState({authorisationRequest}, () => this.isValid('authorisationRequest'));
  }

  toggleModal(){
    this.setState({isModalOpen: !this.state.isModalOpen, selectedAuthorisationRequest: null});
  }

  toggleRevokeModal(){
    this.setState({isRevokeModalOpen: !this.state.isRevokeModalOpen, revokeAuthorisationRequest: null});
  }

  handleSubmitAuthorisationRequest(e) {
    e.preventDefault();

    if (!this.isValid('authorisationRequest')) {
      return;
    }

    let {locationClientId} = this.state.authorisationRequest;
    let isClient = /^[c|C]\d+$/;
    let isLocation = /^[l|L]\d+$/;

    let authRequest = {
      toLocationId: isLocation.test(locationClientId) ? parseInt(locationClientId.replace(/^[l|L]/, '')) : null,
      toClientId: isClient.test(locationClientId) ? parseInt(locationClientId.replace(/^[c|C]/, '')) : null,
      requestedByUserId: this.props.userId,
      originalRequestInfo: null,
    };

    apiPost('/api/AuthorisationRequests/create', authRequest)
      .then(x => {
        if(x.error){
          x.data.json().then(a => toastr.error(a.message));
        } else {
          toastr.success('Request for access submitted');
          this.loadUser();
          this.props.requestsUpdated();
          this.setState({ isLinkClientLocationModalOpen: false});
        }
      });
  }

  isValid(stateItem){
    let item = this.state[stateItem];
    let messages = {};
    let isValid = true;

    for (let propName in ValidationRules) {

      let rule = ValidationRules[propName];
      let val = item[propName];

      let valResult = rule.validator(val, this.state);
      if (!valResult.isValid) {
        messages[propName] = valResult.message;
        isValid = false;
      }
    }

    this.setState({validationMessages: messages});

    return isValid;
  }

  loadUser() {
    if(this.props.userId) {
      apiRequest(`/api/users/${this.props.userId}`)
        .then(x => this.setState({
          user: x.data,
          authorisationRequest: {
            locationClientId: '',
          },
          validationMessages: {},
          isModalOpen: false,
          isRevokeModalOpen: false,
        }));
    }
  }

  actionRequest(approve = true, ar, reason) {
    let {id} = ar;
    let selectedAuthorisationRequest = {
      authorisationRequest: {...ar, actionType: approve ? 'Accepted' : 'Rejected', reason: !reason ? '' : reason},
    };

    if(!approve && !reason){
      this.setState({isModalOpen: true, selectedAuthorisationRequest});
    }
    else {
      apiPut(`/api/AuthorisationRequests/${id}/action`, selectedAuthorisationRequest)
        .then(x => {
          if (x.data && !x.error) {
            toastr.success(`Authorisation request ${approve ? 'accepted' : 'rejected'}`);
            this.props.requestsUpdated();
            this.loadUser();
          } else {
            toastr.error(`Failed to action request: ${x.error}`);
          }
        });
    }
  }

  revokeRequest(ar, reason) {
    let {id} = ar;
    let revokeAuthorisationRequest = {
      reason: !reason ? '' : reason,
      id: ar.id,
    };

    if (!reason) {
      this.setState({isRevokeModalOpen: true, revokeAuthorisationRequest});
    }
    else {
      apiPut(`/api/AuthorisationRequests/${id}/revoke`, revokeAuthorisationRequest)
        .then(x => {
          if (x.data && !x.error) {
            toastr.success(`Authorisation request revoked`);
            this.props.requestsUpdated();
            this.loadUser();
          } else {
            toastr.error(`Failed to action request: ${x.error}`);
          }
        });
    }
  }

  componentDidMount(){
    this.loadUser();
  }

  render() {
    let {account} = this.props;

    let {user, isModalOpen, isRevokeModalOpen} = this.state;
    if (!user) {
      return null;
    }

    let authRequestColumns = [
      {
        id: 'createdDate',
        Header: 'Requested',
        accessor: c => moment(c.createdDate).format(SHORT_DATE_FORMAT),
        maxWidth: 90,
      },
      {
        id: 'assignTo',
        Header: 'Access To',
        Cell: row => <span>{row.original.toLocationId ?
          <Link to={`${URL_Locations}${row.original.toLocationId}`}>{row.original.toLocationName} (Location)</Link> :
          <Link to={`${URL_Clients}${row.original.toClientId}`}>{row.original.toClientName} (Client)</Link>}
          </span>,
        width: 300,
      },
      {
        Header: 'Assigned To',
        accessor: 'assignedToUserName',
        maxWidth: 100,
      },
      {
        Header: 'Actioned',
        id: 'actionedDate',
        accessor: c => c.actionedDate ? moment(c.actionedDate).format(SHORT_DATE_FORMAT) : '',
        maxWidth: 90,
      },
      {
        Header: 'Actioned By',
        accessor: 'actionedByUserName',
        maxWidth: 100,
      },
      {
        Header: 'Action',
        accessor: 'actionTypeDescription',
        Cell: row => (row.value ?
          <div>{row.value} {(row.original && row.original.reason) ?
            <span>({row.original.reason})</span> : null} </div> :
          (!IsCurrentUser(account, user) && <div>
            <Button
              color="success"
              onClick={() => this.actionRequest(true, row.original)}
              style={{marginRight: '5px'}}
            >
              <i className="fa fa-tick"/>&nbsp;Approve</Button>
            <Button
              color="danger"
              onClick={() => this.actionRequest(false, row.original)}
            >
              <i className="fa fa-tick"/>&nbsp;Reject</Button>
          </div>)),
      },
      {
        id: 'revoke',
        Header: 'Revoked',
        accessor: c => c.revokedByUserId ? `${moment(c.revokedDate).format(SHORT_DATE_FORMAT)} (${c.revokedReason})` : null,
        Cell: row => (row.value ?
          <div>{row.value}</div> :
          (!IsCurrentUser(account, user) && row.original.isActive && row.original.actionType === 1 && <div>
            <Button
              color="danger"
              onClick={() => this.revokeRequest(row.original)}
              style={{marginRight: '5px'}}
            >
              <i className="fa fa-times"/>&nbsp;Revoke</Button>
          </div>)),
      },
    ];

    let hasUserPhone = user && user.userContact && user.userContact.communications.findIndex(x => x.communicationType.toLowerCase().includes('phone')) >= 0;

    return hasUserPhone ? (
        <div className="row">
          <Modal isOpen={isModalOpen} toggle={this.toggleModal}>
            <ModalBody>
              <Form>
                <FormGroup>
                  <Label for="authorisationRequestReason">Reason</Label>
                  {(this.state.selectedAuthorisationRequest && this.state.selectedAuthorisationRequest.authorisationRequest) &&
                  <Input type="textarea"
                         name="authorisationRequestReason"
                         id="authorisationRequestReason"
                         value={this.state.selectedAuthorisationRequest.authorisationRequest.reason}
                         onChange={(e) => this.setState({
                           selectedAuthorisationRequest: {
                             ...this.state.selectedAuthorisationRequest,
                             authorisationRequest: {
                               ...this.state.selectedAuthorisationRequest.authorisationRequest,
                               reason: e.target.value
                             }
                           }
                         })
                         }
                  />}
                </FormGroup>

              </Form>
            </ModalBody>
            <ModalFooter>
              <FormGroup>
                {(this.state.selectedAuthorisationRequest &&
                  this.state.selectedAuthorisationRequest.authorisationRequest && this.state.selectedAuthorisationRequest.authorisationRequest.reason !== '') &&
                <Button color="danger"
                        onClick={() => this.actionRequest(false, this.state.selectedAuthorisationRequest.authorisationRequest, this.state.selectedAuthorisationRequest.authorisationRequest.reason)}>Reject</Button>}
                &nbsp;
                <Button onClick={this.toggleModal}>Cancel</Button>
              </FormGroup>
            </ModalFooter>
          </Modal>

          <Modal isOpen={isRevokeModalOpen} toggle={this.toggleRevokeModal}>
            <ModalBody>
              <Form>
                <FormGroup>
                  <Label for="authorisationRequestReason">Reason for revocation</Label>
                  {this.state.revokeAuthorisationRequest &&
                  <Input type="textarea"
                         name="authorisationRequestRevokeReason"
                         id="authorisationRequestRevokeReason"
                         value={this.state.revokeAuthorisationRequest.reason}
                         onChange={(e) => this.setState({
                           revokeAuthorisationRequest: {
                             ...this.state.revokeAuthorisationRequest,
                             reason: e.target.value
                           }
                         })}

                  />}
                </FormGroup>

              </Form>
            </ModalBody>
            <ModalFooter>
              <FormGroup>
                {(this.state.revokeAuthorisationRequest &&
                  this.state.revokeAuthorisationRequest.reason !== '') &&
                <Button color="danger"
                        onClick={() => this.revokeRequest(this.state.revokeAuthorisationRequest, this.state.revokeAuthorisationRequest.reason)}>Revoke</Button>}
                &nbsp;
                <Button onClick={this.toggleRevokeModal}>Cancel</Button>
              </FormGroup>
            </ModalFooter>
          </Modal>

          <div className="col-6 col-md-12">
            <h5>Authorisation Requests</h5>

            <ReactTable
              sortable={false}
              filterable={false}
              data={user.authorisationRequests}
              columns={authRequestColumns}
              defaultPageSize={10}
              minRows={6}
              style={{marginBottom: '25px'}}
            />

            <div className="form-horizontal col-12">
              {/* <h6>Request access to an existing location or client</h6>
              <TextBoxControl
                name={'locationClientId'}
                labelText={'Location/Client Id'}
                value={this.state.authorisationRequest.locationClientId}
                onChange={this.handleFieldChange}
                validationMessages={this.state.validationMessages}
                showLabel={false}
              />

              <div>
                <Button color="success" onClick={this.handleSubmitAuthorisationRequest}><i
                  className="fa fa-check"/>&nbsp;&nbsp;Create Request</Button>&nbsp;&nbsp;
                {this.props.onCreateNewClient && user.authorisationRequests && user.authorisationRequests.length === 0 &&
                <Button color="secondary" onClick={() => this.props.onCreateNewClient()}><i
                  className="fa fa-plus-circle"/>&nbsp;&nbsp;New Client</Button>}
              </div> */}
            </div>
          </div>
        </div>) :  null;
  }
}


UserAuthenticationRequestsControl.propTypes = {
  account: PropTypes.object.isRequired,
  userId: PropTypes.string.isRequired,
  showUserPhoneWarning: PropTypes.bool.isRequired,
  requestsUpdated: PropTypes.func.isRequired,
  onCreateNewClient: PropTypes.func,
};

export default UserAuthenticationRequestsControl;